<script setup lang="ts">
  import { cva } from 'class-variance-authority'
  import { useFocusWithin } from '@vueuse/core'
  import Icon from '@/components/icon/Index.vue'
  import { useGlobalSettingsStore } from '@/stores/GlobalSettings.store'

  const globalSettingsStore = useGlobalSettingsStore()

  const { searchPageLink } = storeToRefs(globalSettingsStore)

  const searchForm = ref(null)
  const searchInput = ref(null)
  const searchQuery = ref(null)

  const { focused: searchVisible } = useFocusWithin(searchForm)
  const { focused: inputFocused } = useFocusWithin(searchInput)

  const searchBase = computed(() =>
    cva(
      'h-12 max-w-80 hidden border items-between justify-center gap-1 rounded p-3 transition-all duration-300 ease-out data-[focused=true]:border-bt-blue-ui sm:flex sm:flex-row',
      {
        variants: {
          state: {
            true: 'w-80 border-border px-4',
            false: 'w-12 border-transparent'
          }
        }
      }
    )({
      state: searchVisible.value
    })
  )

  const handleSearch = async () => {
    if (searchPageLink.value?.href && searchQuery.value) {
      await navigateTo(
        {
          path: searchPageLink.value?.href,
          query: { search: searchQuery.value }
        },
        { external: true }
      )
    }
  }
</script>

<template>
  <form
    ref="searchForm"
    :class="searchBase"
    :data-focused="inputFocused"
    tabindex="0"
  >
    <input
      ref="searchInput"
      v-model="searchQuery"
      name="search"
      class="w-full px-1 leading-6 transition-all duration-300 ease-out focus-visible:outline-none"
      :class="{ '!px-0': !searchVisible }"
      :placeholder="searchPageLink?.placeholder"
      :aria-label="searchPageLink?.placeholder"
      @keyup.enter="handleSearch"
    />
    <button
      type="button"
      class="flex size-6 items-center justify-center object-left"
      :aria-label="$t('aria.label.search')"
    >
      <Icon name="common/search" />
    </button>
  </form>
</template>
